package se.mittpass.web

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import androidx.compose.runtime.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.renderComposable
import se.mittpass.shared.api.BillingStatusResponse
import se.mittpass.shared.api.createHttpClient
import se.mittpass.shared.auth.AuthStorage
import se.mittpass.shared.repository.AuthRepository

private val appScope = MainScope()

internal val authStorage = AuthStorage()

// onUnauthorized is set after AppContent composition; this var bridges the module-level client
// to the composable state so the session-expiry banner can fire.
private var onSessionExpired: (() -> Unit)? = null
internal var onBillingBlocked: ((String) -> Unit)? = null

internal val httpClient = createHttpClient(
    authStorage,
    onUnauthorized = { onSessionExpired?.invoke() },
    onPaymentRequired = { msg -> onBillingBlocked?.invoke(msg) }
)
val authRepo = AuthRepository(httpClient, authStorage)

data class UserInfo(val userId: Long, val email: String, val role: String)

fun parseJwtUserInfo(): UserInfo? {
    val token = authStorage.getToken() ?: return null
    return try {
        val parts = token.split(".")
        if (parts.size < 2) return null
        var base64 = parts[1].replace('-', '+').replace('_', '/')
        while (base64.length % 4 != 0) base64 += "="
        val payload = window.atob(base64)
        val obj: dynamic = kotlin.js.JSON.parse(payload)
        val userId = (obj.userId as? Number)?.toLong() ?: return null
        val email = obj.email as? String ?: return null
        val role = obj.role as? String ?: return null
        // Check token expiry — if expired, treat as not logged in
        val exp = (obj.exp as? Number)?.toLong()
        if (exp != null && exp * 1000 < js("Date.now()").unsafeCast<Double>().toLong()) {
            authStorage.clear()
            return null
        }
        UserInfo(userId, email, role)
    } catch (_: Exception) { null }
}

fun main() {
    LocaleManager.init()
    ThemeManager.init()
    kotlinx.browser.document.getElementById("root")?.innerHTML = ""
    renderComposable(rootElementId = "root") {
        AppContent()
    }
}

@Composable
private fun AppContent() {
    var isLoggedIn by remember { mutableStateOf(authRepo.isLoggedIn()) }
    var userInfo by remember { mutableStateOf(parseJwtUserInfo()) }
    var screen by remember {
        val path = window.location.pathname.trimEnd('/').ifEmpty { "/" }
        val search = window.location.search
        val verifyToken = if (path == "/verify-email") {
            Regex("[?&]token=([^&]+)").find(search)?.groupValues?.get(1)
        } else null
        val resetToken = if (path == "/reset-password") {
            Regex("[?&]token=([^&]+)").find(search)?.groupValues?.get(1)
        } else null
        mutableStateOf<Screen>(
            when {
                verifyToken != null -> Screen.VerifyEmail(verifyToken)
                resetToken != null -> Screen.ResetPassword(resetToken)
                path == "/privacy" || path == "/integritetspolicy" -> Screen.Privacy
                path == "/terms" || path == "/anvandarvillkor" -> Screen.Terms
                path == "/dpa" -> Screen.Dpa
                path == "/delete-account" || path == "/radera-konto" -> Screen.DeleteAccount
                path == "/support" || path == "/hjalp" -> Screen.Support
                path == "/personalliggare" -> Screen.Personalliggare
                path == "/schemalagga-personal" || path == "/schemalaggning" -> Screen.Schemalagga
                path == "/egenkontroll" -> Screen.Egenkontroll
                path == "/livsmedelskontroll" -> Screen.Livsmedelskontroll
                path == "/haccp" -> Screen.Haccp
                isLoggedIn && userInfo != null -> Screen.Dashboard
                else -> Screen.Landing
            }
        )
    }
    var authDialog by remember { mutableStateOf<AuthMode?>(null) }
    var sessionExpiredMessage by remember { mutableStateOf<String?>(null) }
    var trialWarning by remember { mutableStateOf<String?>(null) }

    // Sync browser URL with current screen — use pushState and update SEO meta + canonical URL
    LaunchedEffect(screen, LocaleManager.currentLanguage) {
        data class PageMeta(val path: String, val title: String, val desc: String)
        val dict = LocaleManager.dictionary
        val meta = when (screen) {
            is Screen.Dashboard -> {
                val curr = window.location.pathname
                val path = if (curr.startsWith("/app")) curr else "/app"
                PageMeta(path, dict.appTitle, "")
            }
            is Screen.Landing -> PageMeta(
                "/",
                "Mittpass – Personalliggare, Schemaläggning & Digital Egenkontroll (HACCP) | 39 kr/anv",
                "Komplett system för personalliggare (Skatteverket SKVFS 2015:6), schemaläggning och digital egenkontroll (HACCP & livsmedelskontroll) för restaurang, café, pizzeria och bar. 39 kr/aktiv användare."
            )
            is Screen.Privacy -> PageMeta("/privacy", "Integritetspolicy – Mittpass", "Integritetspolicy och personuppgiftsbehandling enligt GDPR för Mittpass.")
            is Screen.Terms -> PageMeta("/terms", "Användarvillkor – Mittpass", "Användarvillkor för Mittpass personalhanterings- och egenkontrolltjänst.")
            is Screen.Dpa -> PageMeta("/dpa", "Personuppgiftsbiträdesavtal (DPA) – Mittpass", "Personuppgiftsbiträdesavtal (DPA) enligt GDPR Art. 28 för Mittpass.")
            is Screen.DeleteAccount -> PageMeta("/delete-account", "Radera konto & data – Mittpass", "Information och begäran om borttagning av konto och personuppgifter i Mittpass.")
            is Screen.Support -> PageMeta("/support", "Support & Hjälp – Mittpass", "Kundsupport, FAQ och teknisk hjälp för Mittpass personalliggare, schema och egenkontroll.")
            is Screen.Personalliggare -> PageMeta(
                "/personalliggare",
                "Digital Personalliggare (Skatteverket SKVFS 2015:6) – App & QR | Mittpass",
                "Godkänd digital personalliggare för restaurang, café, pizzeria och catering enligt SKVFS 2015:6. Incheckning via mobil/QR, omedelbar export vid kontroll och 2+ års arkivering. 39 kr/anv."
            )
            is Screen.Schemalagga -> PageMeta(
                "/schemalagga-personal",
                "Schemaläggning för Personal – Vaktlista, Passbyte & App | Mittpass",
                "Smidig schemaläggning på nätet och i mobilen för restaurang, café, pizzeria och bageri. Veckoschema, passbyten, frånvaro, obekväm arbetstid, dygnsvila (11h) och personalliggare i samma app. 39 kr/anv."
            )
            is Screen.Egenkontroll -> PageMeta(
                "/egenkontroll",
                "Digital Egenkontroll för Restaurang & Kök – Temperatur & Rutiner | Mittpass",
                "Byt pärmen mot digital egenkontroll i mobilen. Dagliga temperaturkontroller (kyl, frys, varmhållning), rengöringsrutiner, avvikelsehantering och PDF-rapporter för miljöinspektören. 39 kr/anv."
            )
            is Screen.Livsmedelskontroll -> PageMeta(
                "/livsmedelskontroll",
                "Livsmedelskontroll i Restaurang & Café – Checklista & Förberedelse | Mittpass",
                "Klara kommunens oanmälda livsmedelskontroll utan anmärkning. Se vad miljöinspektören kontrollerar enligt EG 852/2004 och hur du visar upp digital egenkontroll & temperaturloggar på 30 sekunder."
            )
            is Screen.Haccp -> PageMeta(
                "/haccp",
                "Digitalt HACCP-system & Temperaturkontroll (CCP) för Restaurang | Mittpass",
                "Komplett digitalt HACCP-system för restaurang, café och storkök. Kritiska styrpunkter (CCP), automatiska temperaturgränser för kyl/frys/nedkylning, avvikelseåtgärder och revisionsklara loggar."
            )
            else -> null
        }
        if (meta != null) {
            if (window.location.pathname != meta.path) window.history.pushState(null, "", meta.path)
            kotlinx.browser.document.title = meta.title
            val descEl = kotlinx.browser.document.querySelector("meta[name=description]")
            if (descEl != null && meta.desc.isNotEmpty()) descEl.setAttribute("content", meta.desc)
            val canonicalUrl = if (meta.path == "/") "https://mittpass.se/" else "https://mittpass.se${meta.path}"
            kotlinx.browser.document.querySelector("link[rel=canonical]")?.setAttribute("href", canonicalUrl)
            kotlinx.browser.document.querySelector("meta[property='og:url']")?.setAttribute("content", canonicalUrl)
            kotlinx.browser.document.querySelector("meta[property='og:title']")?.setAttribute("content", meta.title)
            if (meta.desc.isNotEmpty()) {
                kotlinx.browser.document.querySelector("meta[property='og:description']")?.setAttribute("content", meta.desc)
            }
            // GA4 SPA page_view (with static English title)
            val englishTitle = LocaleManager.getEnglishTitle(meta.path)
            js("if(typeof gtag !== 'undefined') gtag('event', 'page_view', { page_title: englishTitle, page_location: window.location.href, page_path: meta.path })")
        }
    }

    // Auto-verify email if token present in URL (backend redirects here after verification)
    LaunchedEffect(Unit) {
        val verified = Regex("[?&]verified=true").containsMatchIn(window.location.search)
        if (verified && !isLoggedIn) {
            // Replace URL to remove token/verified param, keep landing page clean
            window.history.replaceState(null, "", "/")
        }
    }
    DisposableEffect(Unit) {
        val handler: (org.w3c.dom.events.Event) -> Unit = {
            // popstate fires when user presses Back/Forward
            val path = window.location.pathname.trimEnd('/').ifEmpty { "/" }
            when {
                path == "/app" || path.startsWith("/app/") -> {
                    if (authRepo.isLoggedIn()) screen = Screen.Dashboard
                    else authDialog = AuthMode.LOGIN
                }
                path == "/privacy" || path == "/integritetspolicy" -> screen = Screen.Privacy
                path == "/terms" || path == "/anvandarvillkor" -> screen = Screen.Terms
                path == "/dpa" -> screen = Screen.Dpa
                path == "/delete-account" || path == "/radera-konto" -> screen = Screen.DeleteAccount
                path == "/support" || path == "/hjalp" -> screen = Screen.Support
                path == "/personalliggare" -> screen = Screen.Personalliggare
                path == "/schemalagga-personal" || path == "/schemalaggning" -> screen = Screen.Schemalagga
                path == "/egenkontroll" -> screen = Screen.Egenkontroll
                path == "/livsmedelskontroll" -> screen = Screen.Livsmedelskontroll
                path == "/haccp" -> screen = Screen.Haccp
                else -> { screen = Screen.Landing; authDialog = null }
            }
        }
        window.addEventListener("popstate", handler)
        onDispose { window.removeEventListener("popstate", handler) }
    }

    fun doLogin() {
        userInfo = parseJwtUserInfo()
        isLoggedIn = true
        authDialog = null
        screen = Screen.Dashboard
        sessionExpiredMessage = null
    }

    fun doLogout() {
        authRepo.logout()
        userInfo = null
        isLoggedIn = false
        screen = Screen.Landing
        authDialog = null
    }

    // Register the callback so the module-level httpClient can signal expiry
    onSessionExpired = {
        sessionExpiredMessage = "Din session har gått ut. Logga in igen."
        doLogout()
    }

    // Check trial expiry when dashboard is shown
    LaunchedEffect(screen) {
        if (screen is Screen.Dashboard && authRepo.isLoggedIn()) {
            try {
                val status: BillingStatusResponse = httpClient.get("billing/status").body()
                val days = status.daysLeftInTrial
                trialWarning = when {
                    status.planStatus == "trial" && days != null && days <= 3 && days >= 0 ->
                        "Provperioden avslutas om $days dagar. Gå till Fakturering för att aktivera ditt konto."
                    status.planStatus == "trial" && (days == null || days < 0) ->
                        "Provperioden har gått ut. Gå till Fakturering för att aktivera ditt konto."
                    status.planStatus == "past_due" ->
                        "Betalning förfallen — uppdatera din betalningsmetod under Fakturering."
                    else -> null
                }
            } catch (_: Exception) { /* billing endpoint not critical */ }
        } else if (screen !is Screen.Dashboard) {
            trialWarning = null
        }
    }

    // Session-expired banner
    sessionExpiredMessage?.let { msg ->
        Div({
            style {
                property("position", "fixed"); property("top", "1rem"); property("left", "50%")
                property("transform", "translateX(-50%)"); property("z-index", "9999")
                property("background", "var(--rp-love)"); property("color", "white")
                property("padding", "0.75rem 1.5rem"); property("border-radius", "50px")
                property("font-weight", "700"); property("font-size", "0.9rem")
                property("box-shadow", "0 4px 20px rgba(0,0,0,0.2)")
                property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem")
            }
        }) {
            FaIcon("fa-solid fa-circle-exclamation", size = "0.9rem", color = "var(--rp-surface)")
            Text(msg)
        }
        LaunchedEffect(msg) {
            kotlinx.coroutines.delay(5000)
            sessionExpiredMessage = null
        }
    }

    // Trial expiry warning banner
    trialWarning?.let { msg ->
        Div({
            style {
                property("position", "fixed"); property("top", "1rem"); property("left", "50%")
                property("transform", "translateX(-50%)"); property("z-index", "9998")
                property("background", "var(--rp-gold)"); property("color", "white")
                property("padding", "0.75rem 1.5rem"); property("border-radius", "50px")
                property("font-weight", "700"); property("font-size", "0.85rem")
                property("box-shadow", "0 4px 20px rgba(0,0,0,0.2)")
                property("max-width", "90vw"); property("text-align", "center")
                property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem")
            }
        }) {
            FaIcon("fa-solid fa-triangle-exclamation", size = "0.9rem", color = "var(--rp-surface)")
            Text(msg)
        }
    }

    // Read-only Demo Mode Banner
    if (isLoggedIn && userInfo?.email == "demo-public@mittpass.se") {
        Div({
            style {
                property("position", "fixed"); property("top", "1rem"); property("left", "50%")
                property("transform", "translateX(-50%)"); property("z-index", "9998")
                property("background", "linear-gradient(135deg, var(--rp-iris), var(--rp-iris))"); property("color", "white")
                property("padding", "0.65rem 1.4rem"); property("border-radius", "50px")
                property("font-weight", "700"); property("font-size", "0.85rem")
                property("box-shadow", "0 6px 24px rgba(109,40,217,0.35)")
                property("display", "flex"); property("align-items", "center"); property("gap", "0.8rem")
                property("max-width", "92vw"); property("text-align", "center")
            }
        }) {
            FaIcon("fa-solid fa-eye", size = "0.9rem", color = "var(--rp-surface)")
            Text("Du kikar på demoläget (skrivskyddat)")
            Button({
                onClick { doLogout(); authDialog = AuthMode.SIGNUP }
                style {
                    property("background", "var(--rp-info-bg)"); property("color", "var(--rp-iris)")
                    property("border", "none"); property("padding", "0.35rem 0.85rem")
                    property("border-radius", "20px"); property("font-weight", "800")
                    property("font-size", "0.78rem"); property("cursor", "pointer")
                    property("white-space", "nowrap")
                }
            }) { Text("Skapa eget konto gratis →") }
        }
    }

    val navigateByKey: (String) -> Unit = { key ->
        window.scrollTo(0.0, 0.0)
        screen = when (key) {
            "personalliggare" -> Screen.Personalliggare
            "schemalagga" -> Screen.Schemalagga
            "egenkontroll" -> Screen.Egenkontroll
            "livsmedelskontroll" -> Screen.Livsmedelskontroll
            "haccp" -> Screen.Haccp
            else -> Screen.Landing
        }
    }

    when (screen) {
        is Screen.Landing -> {
            LandingPage(
                onLogin = {
                    if (isLoggedIn) screen = Screen.Dashboard
                    else authDialog = AuthMode.LOGIN
                },
                onSignUp = { authDialog = AuthMode.SIGNUP },
                onDemoLogin = {
                    appScope.launch {
                        try {
                            authRepo.login(se.mittpass.shared.api.LoginRequest("demo-public@mittpass.se", "demo123"))
                            doLogin()
                        } catch (_: Exception) {}
                    }
                },
                onPrivacy = { screen = Screen.Privacy },
                onTerms = { screen = Screen.Terms },
                onDpa = { screen = Screen.Dpa },
                onNavigate = navigateByKey
            )
        }
        is Screen.Dashboard -> Dashboard(
            userInfo = userInfo,
            onLogout = { doLogout() }
        )
        is Screen.Privacy -> PrivacyPage(onBack = { screen = Screen.Landing })
        is Screen.Terms -> TermsPage(onBack = { screen = Screen.Landing })
        is Screen.Dpa -> DpaPage(onBack = { screen = Screen.Landing })
        is Screen.DeleteAccount -> DeleteAccountPage(onBack = { screen = Screen.Landing })
        is Screen.Support -> SupportPage(onBack = { screen = Screen.Landing })
        is Screen.Personalliggare -> PersonalliggarePage(
            onSignUp = { authDialog = AuthMode.SIGNUP },
            onLogin = { authDialog = AuthMode.LOGIN },
            onNavigate = navigateByKey,
            onPrivacy = { screen = Screen.Privacy }, onTerms = { screen = Screen.Terms }, onDpa = { screen = Screen.Dpa }
        )
        is Screen.Schemalagga -> SchemalaggningSida(
            onSignUp = { authDialog = AuthMode.SIGNUP },
            onLogin = { authDialog = AuthMode.LOGIN },
            onNavigate = navigateByKey,
            onPrivacy = { screen = Screen.Privacy }, onTerms = { screen = Screen.Terms }, onDpa = { screen = Screen.Dpa }
        )
        is Screen.Egenkontroll -> EgenkontrollSida(
            onSignUp = { authDialog = AuthMode.SIGNUP },
            onLogin = { authDialog = AuthMode.LOGIN },
            onNavigate = navigateByKey,
            onPrivacy = { screen = Screen.Privacy }, onTerms = { screen = Screen.Terms }, onDpa = { screen = Screen.Dpa }
        )
        is Screen.Livsmedelskontroll -> LivsmedelskontrollSida(
            onSignUp = { authDialog = AuthMode.SIGNUP },
            onLogin = { authDialog = AuthMode.LOGIN },
            onNavigate = navigateByKey,
            onPrivacy = { screen = Screen.Privacy }, onTerms = { screen = Screen.Terms }, onDpa = { screen = Screen.Dpa }
        )
        is Screen.Haccp -> HaccpSida(
            onSignUp = { authDialog = AuthMode.SIGNUP },
            onLogin = { authDialog = AuthMode.LOGIN },
            onNavigate = navigateByKey,
            onPrivacy = { screen = Screen.Privacy }, onTerms = { screen = Screen.Terms }, onDpa = { screen = Screen.Dpa }
        )
        is Screen.VerifyEmail -> VerifyEmailScreen(
            token = (screen as Screen.VerifyEmail).token,
            onDone = { screen = Screen.Landing; authDialog = AuthMode.LOGIN }
        )
        is Screen.ResetPassword -> ResetPasswordScreen(
            token = (screen as Screen.ResetPassword).token,
            onDone = { screen = Screen.Landing; authDialog = AuthMode.LOGIN }
        )
    }

    // Cookie consent banner — shown to first-time visitors
    CookieConsentBanner()

    // Auth dialog overlay — shown on any public page
    authDialog?.let { mode ->
        AuthDialog(
            mode = mode,
            onSuccess = { doLogin() },
            onSwitchMode = { authDialog = it },
            onDismiss = { authDialog = null },
            onTerms = { screen = Screen.Terms },
            onDpa = { screen = Screen.Dpa }
        )
    }
}

@Composable
private fun VerifyEmailScreen(token: String, onDone: () -> Unit) {
    var status by remember { mutableStateOf<String?>(null) }
    var isError by remember { mutableStateOf(false) }

    LaunchedEffect(token) {
        authRepo.verifyEmail(token).fold(
            onSuccess = { status = "E-postadressen har verifierats! Du kan nu logga in." },
            onFailure = { isError = true; status = "Länken är ogiltig eller har gått ut." }
        )
    }

    Div({ style {
        property("min-height", "100vh"); property("display", "flex"); property("align-items", "center")
        property("justify-content", "center"); property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
    } }) {
        Div({ style {
            property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "2.5rem 2rem")
            property("max-width", "420px"); property("width", "90%"); property("text-align", "center")
        } }) {
            if (status == null) {
                Div({ style { property("color", "var(--rp-subtle)"); property("font-size", "1rem") } }) { Text("Verifierar…") }
            } else {
                Div({ style { property("margin-bottom", "1rem") } }) {
                    if (isError) {
                        FaIcon("fa-solid fa-circle-xmark", size = "2.5rem", color = "var(--rp-err-text)")
                    } else {
                        FaIcon("fa-solid fa-circle-check", size = "2.5rem", color = "var(--rp-ok-text)")
                    }
                }
                Div({ style {
                    property("font-weight", "700"); property("font-size", "1.1rem")
                    property("color", if (isError) "var(--rp-err-text)" else "var(--rp-ok-text)")
                    property("margin-bottom", "1.5rem")
                } }) { Text(status!!) }
                Button({
                    onClick { onDone() }
                    style {
                        property("padding", "0.75rem 2rem"); property("border", "none")
                        property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                        property("color", "white"); property("border-radius", "50px")
                        property("font-weight", "700"); property("cursor", "pointer"); property("font-family", "inherit")
                        property("font-size", "1rem")
                    }
                }) { Text(if (isError) "Tillbaka" else "Logga in") }
            }
        }
    }
}

@Composable
private fun ResetPasswordScreen(token: String, onDone: () -> Unit) {
    var password by remember { mutableStateOf("") }
    var confirm by remember { mutableStateOf("") }
    var status by remember { mutableStateOf<String?>(null) }
    var isError by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(false) }

    Div({ style {
        property("min-height", "100vh"); property("display", "flex"); property("align-items", "center")
        property("justify-content", "center"); property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
    } }) {
        Div({ style {
            property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "2.5rem 2rem")
            property("max-width", "420px"); property("width", "90%")
        } }) {
            if (status != null) {
                Div({ style { property("text-align", "center") } }) {
                    Div({ style { property("margin-bottom", "1rem") } }) {
                        if (isError) {
                            FaIcon("fa-solid fa-circle-xmark", size = "2.5rem", color = "var(--rp-err-text)")
                        } else {
                            FaIcon("fa-solid fa-circle-check", size = "2.5rem", color = "var(--rp-ok-text)")
                        }
                    }
                    Div({ style {
                        property("font-weight", "700"); property("font-size", "1.1rem")
                        property("color", if (isError) "var(--rp-err-text)" else "var(--rp-ok-text)")
                        property("margin-bottom", "1.5rem")
                    } }) { Text(status!!) }
                    Button({
                        onClick { onDone() }
                        style {
                            property("padding", "0.75rem 2rem"); property("border", "none")
                            property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                            property("color", "white"); property("border-radius", "50px")
                            property("font-weight", "700"); property("cursor", "pointer"); property("font-family", "inherit")
                        }
                    }) { Text(if (isError) "Tillbaka" else "Logga in") }
                }
            } else {
                H3({ style { property("margin", "0 0 0.25rem 0"); property("font-size", "1.4rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                    Text("Sätt ditt lösenord")
                }
                P({ style { property("margin", "0 0 1.5rem 0"); property("color", "var(--rp-muted)"); property("font-size", "0.85rem") } }) {
                    Text("Välj ett lösenord för ditt Mittpass-konto.")
                }
                Div({ style { property("margin-bottom", "1rem") } }) {
                    Label(attrs = { style { property("display", "block"); property("font-weight", "700"); property("font-size", "0.78rem"); property("color", "var(--rp-text)"); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "0.4rem") } }) { Text("Nytt lösenord") }
                    Input(InputType.Password) {
                        value(password); onInput { password = it.value }
                        attr("placeholder", "Minst 8 tecken")
                        style { property("width", "100%"); property("padding", "0.75rem 1rem"); property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "12px"); property("font-size", "0.95rem"); property("outline", "none"); property("box-sizing", "border-box"); property("font-family", "inherit") }
                    }
                }
                Div({ style { property("margin-bottom", "1.5rem") } }) {
                    Label(attrs = { style { property("display", "block"); property("font-weight", "700"); property("font-size", "0.78rem"); property("color", "var(--rp-text)"); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "0.4rem") } }) { Text("Bekräfta lösenord") }
                    Input(InputType.Password) {
                        value(confirm); onInput { confirm = it.value }
                        attr("placeholder", "Upprepa lösenordet")
                        style { property("width", "100%"); property("padding", "0.75rem 1rem"); property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "12px"); property("font-size", "0.95rem"); property("outline", "none"); property("box-sizing", "border-box"); property("font-family", "inherit") }
                    }
                }
                Button({
                    onClick {
                        if (!isValidPassword(password)) { isError = true; status = "Lösenordet måste vara minst 8 tecken långt och innehålla en stor bokstav, en liten bokstav, en siffra samt ett specialtecken."; return@onClick }
                        if (password != confirm) { isError = true; status = "Lösenorden matchar inte."; return@onClick }
                        loading = true
                        appScope.launch {
                            authRepo.resetPassword(token, password).fold(
                                onSuccess = { isError = false; status = "Lösenordet är satt! Du kan nu logga in." },
                                onFailure = { isError = true; status = "Länken är ogiltig eller har gått ut." }
                            )
                            loading = false
                        }
                    }
                    style {
                        property("width", "100%"); property("padding", "0.85rem"); property("border", "none")
                        property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                        property("color", "white"); property("border-radius", "50px")
                        property("font-weight", "700"); property("font-size", "1rem"); property("cursor", "pointer"); property("font-family", "inherit")
                    }
                }) { Text(if (loading) "Sparar…" else "Spara lösenord") }
            }
        }
    }
}

private sealed class Screen {
    data object Landing : Screen()
    data object Dashboard : Screen()
    data object Privacy : Screen()
    data object Terms : Screen()
    data object Dpa : Screen()
    data object DeleteAccount : Screen()
    data object Support : Screen()
    data object Personalliggare : Screen()
    data object Schemalagga : Screen()
    data object Egenkontroll : Screen()
    data object Livsmedelskontroll : Screen()
    data object Haccp : Screen()
    data class VerifyEmail(val token: String) : Screen()
    data class ResetPassword(val token: String) : Screen()
}

private fun isValidPassword(password: String): Boolean {
    val length = password.length >= 8
    val hasDigit = password.any { it.isDigit() }
    val hasUpper = password.any { it.isUpperCase() }
    val hasLower = password.any { it.isLowerCase() }
    val hasSpecial = password.any { !it.isLetterOrDigit() }
    return length && hasDigit && hasUpper && hasLower && hasSpecial
}
